import React, { useMemo } from "react";
import { useNavigate } from "react-router";
import { Folder, ViewList } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Chip, Divider, Typography } from "@mui/material";

import { MultiProject } from "fond/types";
import { LinearProgressWithLabel, StackedNavigationButton } from "fond/widgets";

import { ViewButton } from "./CarvePanel.styles";

interface CarvePanelProps {
  progress?: number | null;
  multiProject: MultiProject;
}

const CarvePanel: React.FC<CarvePanelProps> = ({ progress = null, multiProject }: CarvePanelProps) => {
  const navigate = useNavigate();
  const areasWithProjects = useMemo(() => multiProject?.Areas.filter((area) => area.Project?.ID).map(({ ID }) => ID), [multiProject]);
  const allProjectsCreated = multiProject?.Areas.length === areasWithProjects.length;
  const isCreatingProjects = progress !== null && progress !== 1;
  const availableSubareaCount = multiProject.Areas.length - areasWithProjects.length;

  const handleOnViewButtonClick = () => {
    navigate(`/cities/${multiProject?.Folder.ID}`);
  };

  return (
    <>
      {areasWithProjects.length === 0 && <Typography variant="subtitle2">No plan generated</Typography>}

      {isCreatingProjects && (
        <Alert
          severity="info"
          variant="compact"
          sx={{
            mb: 2,
            "& .MuiAlert-message": {
              width: "100%",
            },
          }}
        >
          <AlertTitle>Creating projects...</AlertTitle>
          <LinearProgressWithLabel value={progress * 100} variant="determinate" />
        </Alert>
      )}

      {areasWithProjects.length > 0 && (
        <>
          <Typography variant="subtitle2">Projects created</Typography>
          <Typography variant="caption">{areasWithProjects.length} subarea projects have been created</Typography>
          <Divider sx={{ mt: 2 }} />
          <ViewButton startIcon={<Folder color="primary" />} onClick={handleOnViewButtonClick} fullWidth>
            View market lens projects
          </ViewButton>
          <Divider />
        </>
      )}

      {!allProjectsCreated && (
        <>
          {areasWithProjects.length > 0 && (
            <Typography variant="subtitle2" sx={{ mt: 3 }}>
              Generate additional projects
            </Typography>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="caption">Confirm the subareas you want to include in your market lens plan.</Typography>
          </Box>

          <StackedNavigationButton
            data-testid="select-subareas-navigation"
            icon={<ViewList />}
            title="Select available subareas"
            target="subareaList"
            disabled={isCreatingProjects}
            rightElement={
              <Chip
                size="small"
                label={
                  <Typography variant="body3" fontWeight={400}>
                    {availableSubareaCount}
                  </Typography>
                }
                sx={{ mr: 1 }}
              />
            }
            sx={{ m: 0, mt: 1 }}
          />
        </>
      )}
    </>
  );
};

export default CarvePanel;
